import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { Sprout, Menu, X, Leaf, Phone, TreePine } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"

export const PublicNavbar: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12)
    window.addEventListener("scroll", onScroll)
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "h-14 bg-[#111]/95 backdrop-blur-md border-b border-white/10 shadow-lg shadow-black/30"
            : "h-16 bg-transparent"
        }`}
      >
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-8 h-8 rounded-xl bg-emerald-500 flex items-center justify-center shadow-sm shadow-emerald-500/30 group-hover:bg-emerald-400 transition-colors">
              <Sprout className="w-4 h-4 text-black" />
            </div>
            <div className="hidden sm:block">
              <span className="font-bold text-white text-sm leading-none block tracking-tight">
                Farmer Tree Registry
              </span>
              <span className="text-[10px] text-neutral-400 leading-none tracking-wide">
                National Tree Inventory System
              </span>
            </div>
            <span className="sm:hidden font-bold text-white text-sm">FTR</span>
          </Link>

          {/* Center nav links */}
          <nav className="hidden lg:flex items-center gap-0.5">
            {[
              { label: "About",        href: "#about",        icon: <Leaf className="w-3.5 h-3.5" /> },
              { label: "How It Works", href: "#how-it-works", icon: <TreePine className="w-3.5 h-3.5" /> },
              { label: "Contact",      href: "#contact",      icon: <Phone className="w-3.5 h-3.5" /> },
            ].map(({ label, href, icon }) => (
              <a
                key={href}
                href={href}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium text-neutral-400 hover:text-white hover:bg-white/5 border border-transparent hover:border-white/10 transition-all"
              >
                {icon}
                {label}
              </a>
            ))}
          </nav>

          {/* Right */}
          <div className="flex items-center gap-2">
            <Link
              to="/register"
              className="hidden sm:flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-bold rounded-lg transition-all hover:scale-105 active:scale-95 shadow-sm shadow-emerald-500/30"
            >
              <Sprout className="w-3.5 h-3.5" />
              Register Your Farm
            </Link>

            {/* Mobile toggle */}
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden h-9 w-9 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-neutral-300 hover:text-white transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[90] lg:hidden"
            />

            {/* Drawer */}
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 34 }}
              className="fixed top-0 right-0 h-full w-[280px] max-w-[90vw] bg-[#161616] border-l border-white/10 z-[100] lg:hidden shadow-2xl flex flex-col"
            >
              {/* Drawer Header */}
              <div className="flex items-center justify-between px-5 h-16 border-b border-white/10">
                <Link
                  to="/"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2"
                >
                  <div className="w-7 h-7 rounded-lg bg-emerald-500 flex items-center justify-center">
                    <Sprout className="w-3.5 h-3.5 text-black" />
                  </div>
                  <span className="font-bold text-white text-sm">FTR System</span>
                </Link>
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setMobileOpen(false)}
                  className="h-8 w-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-neutral-400 hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </motion.button>
              </div>

              {/* Drawer Links */}
              <nav className="flex-1 py-4 px-3">
                <div className="mb-3 px-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-600">
                    Navigation
                  </span>
                </div>
                {[
                  { label: "About",        href: "#about",        icon: <Leaf className="w-4 h-4" /> },
                  { label: "How It Works", href: "#how-it-works", icon: <TreePine className="w-4 h-4" /> },
                  { label: "Contact",      href: "#contact",      icon: <Phone className="w-4 h-4" /> },
                ].map((item, i) => (
                  <motion.a
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.06 }}
                    className="flex items-center gap-3 h-11 px-3 rounded-lg text-sm font-medium text-neutral-400 hover:text-white hover:bg-white/5 transition-colors mb-0.5"
                  >
                    <span className="text-neutral-600">{item.icon}</span>
                    {item.label}
                  </motion.a>
                ))}
              </nav>

              {/* Drawer CTA */}
              <div className="px-4 pb-8 pt-4 border-t border-white/10 space-y-2">
                <Link
                  to="/register"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-2 w-full py-3 bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold rounded-xl transition-colors"
                >
                  <Sprout className="w-4 h-4" />
                  Register Your Farm
                </Link>
                <p className="text-center text-[10px] text-neutral-600">
                  Free · No account needed
                </p>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  )
}

export default PublicNavbar